import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button, Input } from '@scio3/ui';
import type { SortDirection } from '@scio3/shared-types';
import { FILE_TYPES } from '@scio3/shared-types';
import { SortDropdown, COMMON_SORT_OPTIONS, AssetTypeFilterDropdown } from '@/components/shared';

/** Filter values for the asset picker type dropdown */
export const PICKER_TYPE_ALL = 'all' as const;
export const PICKER_TYPE_SHARED = 'shared_with_me' as const;

/** File types that map directly to a fileType query filter */
export const PICKER_FILE_TYPE_FILTERS = [
  FILE_TYPES.IMAGE,
  FILE_TYPES.VIDEO,
  FILE_TYPES.DOCUMENT,
  FILE_TYPES.WEB,
] as const;

interface AssetPickerSearchProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  typeFilter: string;
  onTypeFilterChange: (type: string) => void;
  sortField: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
  folderLevelSecurity?: boolean;
}

export function AssetPickerSearch({
  searchText,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  sortField,
  sortDirection,
  onSort,
  folderLevelSecurity,
}: AssetPickerSearchProps) {
  const { t } = useTranslation();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSearchInput = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Adapt between parent's string-based typeFilter and shared component's value/shared split
  const isShared = typeFilter === PICKER_TYPE_SHARED;
  const filterValue = typeFilter === PICKER_TYPE_ALL || isShared ? null : typeFilter;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('Search')}
          defaultValue={searchText}
          onChange={(e) => handleSearchInput(e.target.value)}
          className="h-8 pl-9 pr-8 text-sm"
        />
        {searchText && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
            onClick={() => onSearchChange('')}
            aria-label={t('Clear search')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Type filter icon button */}
      <AssetTypeFilterDropdown
        value={filterValue}
        onChange={(val) => onTypeFilterChange(val ?? PICKER_TYPE_ALL)}
        showShared={folderLevelSecurity}
        sharedActive={isShared}
        onSharedChange={(shared) => {
          if (shared) onTypeFilterChange(PICKER_TYPE_SHARED);
        }}
        trigger="icon"
        align="end"
      />

      {/* Sort icon button */}
      <SortDropdown
        options={COMMON_SORT_OPTIONS}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    </div>
  );
}
